import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Fuel, RefreshCw, Loader2, Check, Globe, Info } from "lucide-react";

// Yakıt fiyatları yönetimi — FX kurları gibi Sistem Ayarları içinde. Lojistik sayfası salt-okunur gösterir.
export default function FuelSettings() {
  const { can } = useAuth();
  const canManage = can("logistics.manage");
  const [prices, setPrices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [manual, setManual] = useState({});
  const [savingF, setSavingF] = useState(null);

  const load = () => api.get("/fuel").then((r) => setPrices(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    try { const { data } = await api.post("/fuel/refresh"); setPrices(data); toast.success("Yakıt fiyatları web'den güncellendi"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Güncellenemedi"); }
    finally { setRefreshing(false); }
  };
  const saveFuel = async (f) => {
    setSavingF(f);
    const val = parseFloat(String(manual[f]).replace(",", "."));
    try {
      const { data } = await api.put(`/fuel/${f}`, { manual_price: isNaN(val) ? null : val });
      setPrices(data);
      setManual((m) => ({ ...m, [f]: "" }));
      toast.success(isNaN(val) ? "Manuel fiyat kaldırıldı (web'den)" : "Yakıt fiyatı kaydedildi");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Kaydedilemedi"); }
    finally { setSavingF(null); }
  };

  const fuels = prices?.fuels || {};
  const keys = Object.keys(fuels);

  return (
    <div className="space-y-4 fade-in" data-testid="fuel-settings-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Fuel className="h-5 w-5 text-brand-green" />
          <div><p className="overline">Lojistik</p><h1 className="font-heading text-2xl font-bold text-slate-900">Yakıt Fiyatları</h1></div>
        </div>
        {canManage && (
          <Button data-testid="fuel-refresh-btn" onClick={refresh} disabled={refreshing} className="bg-brand-green text-white hover:bg-brand-green-600">
            {refreshing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Web'den Güncelle
          </Button>
        )}
      </div>

      <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <p>Sevk emri yakıt maliyeti bu fiyatlarla <b>otomatik</b> hesaplanır. Fiyatlar canlı web ortalamasından gelir; istersen <b>manuel</b> sabitleyebilirsin. Lojistik sayfasında yalnızca <b>salt-okunur</b> gösterilir.</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {[0, 1].map((i) => <div key={i} className="h-40 animate-pulse rounded-md bg-slate-200/60" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {keys.map((f) => {
            const c = fuels[f] || {};
            return (
              <div key={f} data-testid={`fuel-card-${f}`} className="rounded-md bg-white p-4 ring-1 ring-inset ring-slate-200">
                <div className="mb-3 flex items-center justify-between">
                  <p className="font-heading text-lg font-bold text-slate-800">{c.label || f}</p>
                  <span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-bold ${c.manual ? "bg-brand-orange text-white" : "bg-slate-100 text-slate-500"}`}>
                    {c.manual ? "MANUEL" : (c.source || "canlı")}
                  </span>
                </div>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Piyasa Ort.</span><span className="tabular font-medium text-slate-700">{(c.market_avg ?? 0).toFixed(2)} ₺/lt</span></div>
                  <div className="flex justify-between border-t border-slate-100 pt-1.5"><span className="font-semibold text-slate-700">Kullanılan</span><span data-testid={`fuel-used-${f}`} className="tabular text-base font-bold text-amber-700">{(c.used ?? 0).toFixed(2)} ₺/lt</span></div>
                </div>
                {Array.isArray(c.breakdown) && c.breakdown.length > 0 && (
                  <div data-testid={`fuel-breakdown-${f}`} className="mt-2 space-y-0.5 rounded-md bg-slate-50 px-2.5 py-1.5">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Kaynak kırılımı ({c.breakdown.length})</p>
                    {c.breakdown.map((b) => (
                      <div key={b.source} className="flex justify-between text-[11px]"><span className="text-slate-500">{b.source}</span><span className="tabular text-slate-600">{Number(b.price).toFixed(2)} ₺</span></div>
                    ))}
                  </div>
                )}
                {canManage && (
                  <div className="mt-3 flex gap-2">
                    <Input data-testid={`fuel-manual-${f}`} type="number" step="0.01" value={manual[f] ?? ""}
                      onChange={(e) => setManual((m) => ({ ...m, [f]: e.target.value }))}
                      placeholder={c.manual ? `Manuel: ${c.manual}` : "Manuel ₺/lt"} className="h-9 tabular" />
                    <Button data-testid={`fuel-save-${f}`} onClick={() => saveFuel(f)} disabled={savingF === f} size="sm" variant="outline" className="h-9 shrink-0">
                      {savingF === f ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    </Button>
                  </div>
                )}
                {canManage && c.manual != null && (
                  <button data-testid={`fuel-clear-${f}`} onClick={() => { setManual((m) => ({ ...m, [f]: "" })); saveFuel(f); }}
                    className="mt-1.5 text-[11px] text-slate-400 hover:text-red-500">manuel fiyatı kaldır (web'e dön)</button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-start gap-2 rounded-md bg-slate-50 p-3 text-[11px] text-slate-500">
        <Globe className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
        <p data-testid="fuel-sources">
          Canlı ortalama {prices?.source_count ?? 0} kaynaktan hesaplanır
          {prices?.sources?.length ? ` — ${prices.sources.join(", ")}` : " (kaynaklar çevrimdışıysa varsayılan fiyatlar kullanılır)"}.
        </p>
      </div>
    </div>
  );
}
